import { useTranslation } from 'react-i18next'
import { Button, Grid, Stack, TextField } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import type Generator from '../types/Generator'
import { useGeneratorForm } from '../hooks/generator'
import { usePolicy } from '../contexts/PolicyContext'
import GeneratorPowerSourceSelect from './GeneratorPowerSourceSelect'
import GeneratorNominalVoltageSelect from './GeneratorNominalVoltageSelect'
import GeneratorNominalCurrentAutocomplete from './GeneratorNominalCurrentAutocomplete'
import CatalogProductAutocomplete from './CatalogProductAutocomplete'
import { CatalogProductType } from '../enums/CatalogProductType'
import { isGeneratorCatalogProduct } from '../utils/catalogProduct'
import { Add } from '@mui/icons-material'
import { useState } from 'react'
import { GeneratorDeviceCatalogProductForm } from './GeneratorDeviceCatalogProductForm'
import GeneratorApparentPowerField from './GeneratorApparentPowerField'
import GeneratorRelayThresholdFields from './GeneratorRelayThresholdFields'

export interface GeneratorFieldFormProps {
  generator: Generator
  field: string
  submitButtonText?: string
  cancelButtonText?: string
  onCancel?: () => void
  afterSubmit?: (generator: Generator) => void
  afterError?: (error: unknown) => void
}

export default function GeneratorFieldForm({
  generator,
  field,
  submitButtonText,
  cancelButtonText,
  onCancel,
  afterSubmit,
  afterError
}: GeneratorFieldFormProps) {
  const { t } = useTranslation('generator')

  const { canCreateCatalogProduct } = usePolicy()

  const [showCatalogProductAutocomplete, setShowCatalogProductAutocomplete] =
    useState<boolean>(true)

  const {
    values,
    errors,
    touched,
    isSubmitting,
    isError,
    getHelperText,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit
  } = useGeneratorForm({
    generator,
    field,
    afterSubmit,
    afterError
  })

  if (field === 'catalogProduct' && !showCatalogProductAutocomplete) {
    return (
      <GeneratorDeviceCatalogProductForm
        generator={generator}
        onCancel={onCancel}
        afterSubmit={(updatedGenerator) => {
          setShowCatalogProductAutocomplete(true)
          afterSubmit?.(updatedGenerator)
        }}
      />
    )
  }

  const shouldDisplayThresholdField =
    field === 'additionalLoad' ||
    field === 'relayOnThreshold' ||
    field === 'relayOffThreshold' ||
    field === 'relayOnDelay' ||
    field === 'relayOffDelay' ||
    field === 'loadAutomationMode'

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          {field === 'catalogProduct' && (
            <>
              {showCatalogProductAutocomplete ? (
                <>
                  <CatalogProductAutocomplete
                    type={CatalogProductType.GENERATOR}
                    label={t('catalogProduct')}
                    value={
                      typeof values.catalogProduct === 'string'
                        ? values.catalogProduct
                        : values.catalogProduct?.id
                    }
                    // error={isError('catalogProduct')}
                    // helperText={getHelperText('catalogProduct')}
                    disabled={isSubmitting}
                    fullWidth
                    onBlur={handleBlur}
                    onChange={(_, value) => {
                      setFieldValue('catalogProduct', value)
                      setFieldTouched('catalogProduct', true)
                    }}
                  />

                  {(values?.catalogProduct === undefined || values.catalogProduct === null) &&
                    canCreateCatalogProduct() && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-start"
                        sx={{ mt: 2 }}
                      >
                        <Button
                          variant="text"
                          color="primary"
                          size="small"
                          endIcon={<Add />}
                          onClick={() => setShowCatalogProductAutocomplete(false)}
                        >
                          {t('actions:addNew')}
                        </Button>
                      </Stack>
                    )}
                </>
              ) : (
                <GeneratorDeviceCatalogProductForm
                  generator={generator}
                  onCancel={() => setShowCatalogProductAutocomplete(true)}
                  afterSubmit={(updatedGenerator) => {
                    setShowCatalogProductAutocomplete(true)
                    setFieldValue('catalogProduct', updatedGenerator.catalogProduct?.id ?? '')
                    afterSubmit?.(updatedGenerator)
                  }}
                />
              )}
            </>
          )}

          {field === 'catalogProduct.name' && typeof values.catalogProduct !== 'string' && (
            <CatalogProductAutocomplete
              type={CatalogProductType.GENERATOR}
              value={values?.catalogProduct?.name}
              clearOnBlur={false}
              label={t('catalogProduct:name')}
              onChange={(_, option) => {
                if (option !== null && typeof option !== 'string') {
                  setFieldValue('catalogProduct.name', option.name)
                  setFieldTouched('catalogProduct.name', true)

                  setFieldValue('catalogProduct.manufacturer', option.manufacturer)
                  setFieldTouched('catalogProduct.manufacturer', true)

                  setFieldValue(
                    'catalogProduct.manufacturerProductCode',
                    option.manufacturerProductCode
                  )
                  setFieldTouched('catalogProduct.manufacturerProductCode', true)

                  if (isGeneratorCatalogProduct(option) && option.customData?.apparentPower) {
                    setFieldValue('apparentPower', option.customData.apparentPower)
                    setFieldTouched('apparentPower', true)
                  }

                  if (isGeneratorCatalogProduct(option) && option.customData?.nominalVoltage) {
                    setFieldValue('nominalVoltage', option.customData.nominalVoltage)
                    setFieldTouched('nominalVoltage', true)
                  }

                  if (isGeneratorCatalogProduct(option) && option.customData?.nominalCurrent) {
                    setFieldValue('nominalCurrent', option.customData.nominalCurrent)
                    setFieldTouched('nominalCurrent', true)
                  }

                  if (isGeneratorCatalogProduct(option) && option.customData?.powerSource) {
                    setFieldValue('powerSource', option.customData.powerSource)
                    setFieldTouched('powerSource', true)
                  }
                }
              }}
              onInputChange={(_, value) => {
                setFieldValue('catalogProduct.name', value)
                setFieldTouched('catalogProduct.name', true)
              }}
            />
          )}

          {field === 'apparentPower' && (
            <GeneratorApparentPowerField
              name="apparentPower"
              value={values.apparentPower}
              error={isError('apparentPower')}
              helperText={getHelperText('apparentPower')}
              disabled={isSubmitting}
              fullWidth
              onBlur={handleBlur}
              onChange={handleChange}
            />
          )}

          {field === 'nominalCurrent' && (
            <GeneratorNominalCurrentAutocomplete
              name="nominalCurrent"
              label={`${t('nominalCurrent')} (A)`}
              value={values.nominalCurrent}
              error={isError('nominalCurrent')}
              helperText={getHelperText('nominalCurrent')}
              disabled={isSubmitting}
              fullWidth
              onBlur={handleBlur}
              onChange={(_, value) => {
                setFieldValue('nominalCurrent', value)
                setFieldTouched('nominalCurrent', true)
              }}
            />
          )}

          {field === 'nominalVoltage' && (
            <GeneratorNominalVoltageSelect
              name="nominalVoltage"
              label={`${t('nominalVoltage')} (V)`}
              value={values.nominalVoltage}
              error={isError('nominalVoltage')}
              helperText={getHelperText('nominalVoltage')}
              disabled={isSubmitting}
              fullWidth
              onBlur={handleBlur}
              onChange={handleChange}
            />
          )}

          {field === 'powerSource' && (
            <GeneratorPowerSourceSelect
              name="powerSource"
              label={t('powerSource')}
              value={values.powerSource}
              error={isError('powerSource')}
              helperText={getHelperText('powerSource')}
              disabled={isSubmitting}
              fullWidth
              onBlur={handleBlur}
              onChange={handleChange}
            />
          )}

          {field === 'customerReference' && (
            <TextField
              name="customerReference"
              label={t('customerReference')}
              value={values.customerReference}
              error={isError('customerReference')}
              helperText={getHelperText('customerReference')}
              disabled={isSubmitting}
              fullWidth
              onBlur={handleBlur}
              onChange={handleChange}
            />
          )}

          {field === 'serialNumber' && (
            <TextField
              name="serialNumber"
              label={t('serialNumber')}
              value={values.serialNumber}
              error={isError('serialNumber')}
              helperText={getHelperText('serialNumber')}
              disabled={isSubmitting}
              fullWidth
              onBlur={handleBlur}
              onChange={handleChange}
            />
          )}

          {shouldDisplayThresholdField && (
            <GeneratorRelayThresholdFields
              values={values}
              errors={errors}
              touched={touched}
              isSubmitting={isSubmitting}
              actualLoadAutomationMode={generator.loadAutomationMode}
              setFieldValue={setFieldValue}
              handleBlur={handleBlur}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
            <Button variant="text" color="inherit" disabled={isSubmitting} onClick={onCancel}>
              {cancelButtonText ?? t('actions:cancel')}
            </Button>

            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              {submitButtonText ?? t('actions:save')}
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>
    </form>
  )
}
